import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import {
  ClientDashboardMetadata,
  ClientLayout,
  ClientLayoutItem,
  ClientLayoutItemNavigate,
  ClientLayoutItemType,
  ClientLayoutNavigateBehaviour,
  DocumentAttachment,
  ListNavigationContext,
  LoadObjectRequest,
  RelationSearchResult,
} from '@mmlogic/components';
import { DataObject } from 'src/app/core/model/data-model/data-object.model';
import { ApiClientService } from 'src/app/core/services/api-client.service';
import { AppService } from 'src/app/core/services/app.service';
import { NotificationService } from 'src/app/core/services/notification.service';

/** Matches field names like `personRef`, `companyRef` — see markRefFieldsAsRelations(). */
const REF_FIELD_NAME = /^[a-zA-Z][a-zA-Z0-9]*Ref$/;
/** A bare API path such as `/persons/9dd2803a-303a-413f-84dc-439e0bc89635`. */
const REF_FIELD_VALUE = /^\/\S+$/;

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent implements OnInit, OnChanges {
  private static tabGroupCounter = 0;
  /** Scopes the tab radio inputs' name/id to this component instance (TASK-0345).
   *  app-dashboard is recursive — an OVERLAY_PAGE mounts a nested app-dashboard as a
   *  DOM sibling, not a separate route. Native radio grouping is by name across the
   *  whole document, so without this suffix a nested instance's tabPage0 radio would
   *  steal the checked state from the parent's active tab, CSS-hiding its content. */
  readonly tabGroupId = `tabs-${DashboardComponent.tabGroupCounter++}`;

  type: string;
  tenantCode: string;
  id: string;

  dataObject: DataObject | undefined;
  /** mayModify/mayDelete for the current record (TASK-0285/TASK-0286), fetched once
   *  per loadDataObject() from the dedicated .../capabilities endpoint — never cached
   *  across navigations, reset in refresh() like dataObject itself. undefined while
   *  loading; the edit/delete/merge affordances below stay hidden until it resolves. */
  capabilities: { mayModify: boolean; mayDelete: boolean } | undefined;
  metadata: ClientDashboardMetadata | undefined;
  mainLayouts: ClientLayout[] = [];
  tabPages: ClientLayout[] = [];
  /** Indexes into tabPages whose content has been shown at least once. The tab
   *  switch itself stays CSS-only (radio + label), but mrd-layout-section for a
   *  tab (and its mrd-table's data fetch) is only mounted once the tab is
   *  visited, so inactive tabs don't load data upfront. */
  visitedTabPages = new Set<number>();
  actions: ClientLayoutItem[] = [];
  /** NAVIGATE items from the object's own dashboard layout (TASK-0319) — only
   *  populated on a record's own detail page (id set), shown in the '...' menu
   *  instead of the inline layout button (see setMetadata()/stripNavigateItems()). */
  navigateItems: ClientLayoutItem[] = [];
  actionsDropdownOpen = false;

  @Input() overlayHref: string | null = null;
  @Output() overlayNavigate = new EventEmitter<{ href: string; listContext?: ListNavigationContext }>();
  @Output() overlayClose = new EventEmitter<void>();
  openOverlayHref: string | null = null;
  /** The query (path/qs/sort) + position of openOverlayHref within it, from the row/document
   *  that was clicked to open the overlay — set only when opened from a list. */
  openOverlayListContext: ListNavigationContext | null = null;
  /** HAL next/prev links for openOverlayHref within openOverlayListContext's query,
   *  fetched via loadOverlayNavLinks() and refreshed after every prev/next step. */
  overlayNavLinks: { next?: { href: string }; prev?: { href: string } } | null = null;
  editingInOverlay = false;
  mergeOverlayOpen = false;
  mergeFields: ClientLayoutItem[] = [];
  deleteConfirmOpen = false;

  /** The current record's archetype file(s) (commons.document/commons.invoice/
   *  commons.email), reported by mrd-layout-section's mrdAttachments — empty
   *  for every other object type. commons.document always reports exactly
   *  one; invoice/email can report several (app-document-preview picks
   *  between them). Drives the "Gegevens"/"Document" split/tab panel below. */
  attachments: DocumentAttachment[] = [];
  activeDocView: 'data' | 'document' = 'data';

  @ViewChild('actionsContainer') actionsContainer: ElementRef;
  @ViewChild('mergeViewEl') mergeViewEl: ElementRef;

  constructor(
    public api: ApiClientService,
    private appService: AppService,
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private destroyRef: DestroyRef
  ) {
    appService
      .getUpdates()
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (this.editingInOverlay) return;
        if (event.type === 'DATA_CHANGE' && this.dataObject && this.dataObject._links._self.href.endsWith('/' + event.id)) {
          if (event.newPath && !this.overlayHref) {
            this.router.navigate(['/data', this.tenantCode, event.newPath, this.id]);
          } else {
            this.refresh();
          }
        }
      });
  }

  get locale(): string {
    return this.appService.getLocale();
  }

  get dataRecord(): Record<string, unknown> {
    return (this.dataObject ?? {}) as unknown as Record<string, unknown>;
  }

  get isSystemRoute(): boolean {
    if (this.overlayHref) {
      return this.overlayHref.startsWith('/system/');
    }
    return !!this.route.snapshot.data['system'];
  }

  get hasModifyAction(): boolean {
    return this.actions.some((a) => a.actionType === 'MODIFY');
  }

  /** ReBAC doesn't cover system.* types (isAdmin getter above) — system routes keep
   *  relying on hasModifyAction/role checks alone, same as before this record ever
   *  carried capabilities. Elsewhere, hidden (false) until capabilities resolves,
   *  same fail-hidden default as mrd-table's own capability check (TASK-0299). */
  get canModify(): boolean {
    return this.isSystemRoute || this.capabilities?.mayModify === true;
  }

  get canDelete(): boolean {
    return this.isSystemRoute || this.capabilities?.mayDelete === true;
  }

  /** REPORT actions only read data (no server-side write), so they stay available
   *  for viewers; REMOVE/MERGE and generic script actions (doAction, POST) don't.
   *  REMOVE additionally needs canDelete, MERGE needs canModify — it PATCHes this
   *  record as the merge winner (TASK-0286). */
  get dropdownActions(): ClientLayoutItem[] {
    const nonModify = this.actions.filter((a) => a.actionType !== 'MODIFY');
    const allowed = nonModify
      .filter((a) => a.actionType !== 'REMOVE' || this.canDelete)
      .filter((a) => a.actionType !== 'MERGE' || this.canModify);
    const actionItems = this.isViewer ? allowed.filter((a) => a.actionType === 'REPORT') : allowed;
    // NAVIGATE items create a related object, same write-gating as the other
    // non-REPORT actions above — hidden for viewers.
    const navigateItems = this.isViewer ? [] : this.navigateItems;
    return [...actionItems, ...navigateItems];
  }

  dropdownItemLabel(item: ClientLayoutItem): string {
    return item.label ?? '';
  }

  get isViewer(): boolean {
    return this.appService.isViewer();
  }

  /** Passed to every mrd-layout-section so it can skip mrdCheckCapabilities/
   *  mrdViewCheckCapabilities entirely for a tenant with no access control configured
   *  (FINDING-0171/TASK-0323), instead of asking once per binding for an answer that's
   *  always yes. Read once per session from AppService, not per dashboard load. */
  get accessControlEnabled(): boolean {
    return this.appService.isAccessControlEnabled();
  }

  /** The active account's linked person record, if any — same source as
   *  object-form.component.ts's `me` getter. Passed to mrd-layout-section so a
   *  top-level memo VIEW can offer "New memo" by falling back to it when there
   *  is no parent record to attach the memo to. mrd-layout-section's `me` prop
   *  is typed as RelationSearchResult ({ id, label }) but — like mrd-form's own
   *  `me` prop — also tolerates this raw { href, name } API link shape. */
  get me(): { href: string; name: string } | null {
    return this.appService.userProfileSubject.getValue()?._links?.person ?? null;
  }

  /** Gates the "who can access this" CTA. The whole /audit/ namespace is admin-only in the
   *  API, so a non-admin must never see it — it would only ever produce a 403. ReBAC only
   *  covers tenant data (system.* types never carry an ACL), so it's also hidden on system
   *  routes. */
  get isAdmin(): boolean {
    return this.appService.isAdmin() && !this.isSystemRoute;
  }

  ngOnInit(): void {
    if (this.overlayHref) {
      this.refresh();
      return;
    }
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refresh());
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refresh());
  }

  // Overlay prev/next (TASK-0160) re-points the same nested <app-dashboard> instance
  // at a new record by reassigning [overlayHref] — ngOnInit only runs once, so without
  // this the component would keep showing the record it first loaded.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['overlayHref'] && !changes['overlayHref'].firstChange && this.overlayHref) {
      this.refresh();
    }
  }

  refresh() {
    this.dataObject = undefined;
    this.capabilities = undefined;
    this.metadata = undefined;
    this.mainLayouts = [];
    this.tabPages = [];
    this.visitedTabPages = new Set<number>();
    this.actions = [];
    this.navigateItems = [];
    this.editingInOverlay = false;
    this.attachments = [];
    this.activeDocView = 'data';

    if (this.overlayHref) {
      const [, , tenantCode, type, id] = this.overlayHref.split('/');
      this.tenantCode = tenantCode;
      this.type = type;
      this.id = id;
      this.setMetadata(this.loadDataObject());
      return;
    }

    const params = this.route.snapshot.params;
    const dashboardName = this.route.snapshot.queryParams['name'];

    this.tenantCode = params['tenantCode'];
    this.type = params['type'];
    this.id = params['id'];

    if (this.id) {
      this.setMetadata(this.loadDataObject());
      return;
    }

    if (this.type) {
      this.setMetadata(
        this.isSystemRoute
          ? this.api.getSystemClassDashboard(this.tenantCode, this.type)
          : this.api.getClassDashboard(this.tenantCode, this.type, dashboardName ?? null)
      );
      return;
    }

    this.setMetadata(
      this.api.getDashboard(
        this.tenantCode,
        dashboardName ? dashboardName : null
      )
    );
  }

  setMetadata(observer: Observable<ClientDashboardMetadata>) {
    observer.subscribe((dashboardMetadata) => {
      this.metadata = dashboardMetadata;
      const refFields: { name: string; href: string }[] = [];
      const layouts = dashboardMetadata.layouts.map((layout) => this.markRefFieldsAsRelations(layout, refFields));
      this.mainLayouts = layouts.filter((layout) => !layout.tabPage);
      this.tabPages = layouts.filter((layout) => layout.tabPage);
      if (this.tabPages.length > 0) {
        this.visitedTabPages.add(0);
      }
      const allItems = [...this.mainLayouts, ...this.tabPages].flatMap((layout) => layout.items);
      this.actions = allItems.filter((item) => item.type === ClientLayoutItemType.ACTION);
      // Only on a record's own detail page (id set) — a CLASS/GENERAL dashboard
      // keeps NAVIGATE items as the inline layout button (see stripNavigateItems below).
      this.navigateItems = this.id ? allItems.filter((item) => item.type === ClientLayoutItemType.NAVIGATE) : [];

      if (this.id) {
        this.mainLayouts = this.mainLayouts.map((layout) => this.stripNavigateItems(layout));
        this.tabPages = this.tabPages.map((layout) => this.stripNavigateItems(layout));
      }

      refFields.forEach(({ name, href }) => this.resolveRefField(name, href));
    });
  }

  /**
   * FINDING-0160 workaround: a plain string field named e.g. `personRef` whose value
   * is a bare API path (`/persons/{id}`) isn't modeled as a RELATION by the backend,
   * so it renders as inert text with no link to the referenced object. Until the
   * backend models these as real relations, relabel matching FIELD items as RELATION
   * client-side (mrd-layout-section then already knows how to render/navigate a
   * relation) and asynchronously resolve the target via resolveRefField().
   */
  private markRefFieldsAsRelations(layout: ClientLayout, refFields: { name: string; href: string }[]): ClientLayout {
    const dataObject = this.dataObject as unknown as Record<string, unknown> | undefined;
    if (!dataObject) return layout;

    const mapItems = (items: ClientLayoutItem[]): ClientLayoutItem[] =>
      items.map((item) => {
        if (item.items) {
          return { ...item, items: mapItems(item.items) };
        }
        if (item.type === ClientLayoutItemType.FIELD && item.name && REF_FIELD_NAME.test(item.name)) {
          const value = dataObject[item.name];
          if (typeof value === 'string' && REF_FIELD_VALUE.test(value)) {
            refFields.push({ name: item.name, href: value });
            return { ...item, type: ClientLayoutItemType.RELATION };
          }
        }
        return item;
      });

    return { ...layout, items: mapItems(layout.items) };
  }

  /** Drops NAVIGATE items from a layout passed to mrd-layout-section on a record's
   *  own detail page (TASK-0319) — they move into the '...' menu instead (see
   *  navigateItems/dropdownActions), so left in place they'd render twice: once as
   *  the inline layout button, once as a menu entry. */
  private stripNavigateItems(layout: ClientLayout): ClientLayout {
    const strip = (items: ClientLayoutItem[]): ClientLayoutItem[] =>
      items
        .filter((item) => item.type !== ClientLayoutItemType.NAVIGATE)
        .map((item) => (item.items ? { ...item, items: strip(item.items) } : item));
    return { ...layout, items: strip(layout.items) };
  }

  /** Resolves a ...Ref field's target object; a 200 injects a `_links` entry so the
   *  relabeled RELATION item can render a working link, a 404 (or any other error)
   *  leaves the field empty. Guards against the object having changed in the
   *  meantime (fast navigation) by checking self href before applying the result. */
  private resolveRefField(fieldName: string, href: string): void {
    const selfHref = this.dataObject?._links?._self?.href;
    this.api
      .getObjectByUrl(this.toDetailHref(href))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (obj) => {
          if (!this.dataObject || this.dataObject._links._self.href !== selfHref) return;
          this.dataObject = {
            ...this.dataObject,
            _links: { ...this.dataObject._links, [fieldName]: { href: this.toDetailHref(obj._links._self.href), name: obj.name } },
          };
        },
        error: () => {
          // 404: reference doesn't resolve (or resolves to something the current
          // user can't read) — leave the RELATION item without a _links entry so
          // mrd-layout-section renders nothing for it.
        },
      });
  }

  /** A `...Ref` field's raw value (e.g. `/persons/{id}`), and the resolved object's
   *  own `_links._self.href`, are both partial — unlike this page's own self href,
   *  they're missing the `/data/{tenant}` route/API prefix, so a bare fetch 404s and
   *  a bare navigate misroutes. */
  private toDetailHref(href: string): string {
    if (href.startsWith('/data/') || href.startsWith('/system/')) return href;
    return `/data/${this.tenantCode}${href}`;
  }

  onTabPageSelected(index: number): void {
    this.visitedTabPages.add(index);
  }

  loadDataObject(): Observable<ClientDashboardMetadata> {
    const object$ = this.overlayHref
      ? this.api.getObjectByUrl(this.overlayHref)
      : this.isSystemRoute
        ? this.api.getObjectByUrl(`/system/${this.tenantCode}/${this.type}/${this.id}`)
        : this.api.getObject(this.tenantCode, this.type, this.id);
    return object$.pipe(
      mergeMap((dataObject) => {
        this.dataObject = dataObject;
        this.loadCapabilities();
        return this.api.getDashboardByUrl(dataObject._links._metadata.href);
      })
    );
  }

  /** Fetches mayModify/mayDelete for the current record (TASK-0285/TASK-0286) from the
   *  dedicated capabilities endpoint — kept separate from the dashboard-metadata load
   *  above so a slow capabilities call never delays the record's own fields from
   *  rendering. System routes carry no ACL (isAdmin getter above), so canModify/
   *  canDelete bypass this entirely there — skip the round trip. Guards against the
   *  record having changed in the meantime (fast navigation), same pattern as
   *  resolveRefField(). Also skipped for a tenant with no access control configured
   *  (FINDING-0171/TASK-0323) — this endpoint is a separate, content-app-only check
   *  (not mrdCheckCapabilities/mrdViewCheckCapabilities), but the same round trip
   *  would always answer true, so it's gated the same way. */
  private loadCapabilities(): void {
    if (this.isSystemRoute || !this.dataObject) return;
    if (!this.accessControlEnabled) {
      this.capabilities = { mayModify: true, mayDelete: true };
      return;
    }
    const selfHref = this.dataObject._links._self.href;
    this.api.getObjectCapabilities(this.tenantCode, this.type, this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (this.dataObject?._links._self.href !== selfHref) return;
          this.capabilities = result;
        },
        error: (err) => {
          if (this.dataObject?._links._self.href !== selfHref) return;
          console.error('[dashboard] capabilities laden mislukt', selfHref, err);
          this.capabilities = { mayModify: false, mayDelete: false };
        },
      });
  }

  onMrdNavigate(event: CustomEvent<{ href?: string; label: string; navigate?: ClientLayoutItemNavigate; navigateBehaviour?: ClientLayoutNavigateBehaviour; listContext?: ListNavigationContext }>) {
    const { href, label, navigate, navigateBehaviour, listContext } = event.detail;
    if (href && navigateBehaviour === ClientLayoutNavigateBehaviour.OVERLAY_PAGE) {
      if (this.overlayHref) {
        this.overlayNavigate.emit({ href, listContext });
      } else {
        this.openOverlayHref = href;
        this.setOverlayListContext(listContext);
      }
      return;
    }

    // A plain (non-overlay) navigation from within an overlay pane leaves the
    // overlay's context entirely, so close it before the router takes over —
    // otherwise the backdrop keeps covering the page the router just navigated to.
    if (this.overlayHref) {
      this.overlayClose.emit();
    }

    if (href) {
      this.router.navigateByUrl(href);
      return;
    }
    if (navigate) {
      if (navigate.navigationKind === 'OBJECT_FORM_DASHBOARD') {
        this.router.navigate(['data', this.tenantCode, navigate.dataClass, 'new']);
      } else if (navigate.navigationKind === 'DASHBOARD') {
        this.router.navigate([this.tenantCode], { queryParams: { name: label } });
      } else if (navigate.navigationKind === 'SYSTEM_DASHBOARD') {
        this.router.navigate(['system', this.tenantCode, navigate.dataClass]);
      } else {
        this.router.navigate([this.tenantCode, navigate.dataClass]);
      }
    }
  }

  /** A NAVIGATE item invoked from the detail page's '...' menu (TASK-0319). When it
   *  carries relatedClass, build the create-form URL from the item's own declared
   *  class + relatedClass and the current record's id — not the viewed object's own
   *  type — e.g. a company page's "Add sales dossier" (dataClass: prospects,
   *  relatedClass: salesDossiers) opens .../prospects/{companyId}/salesDossiers/new.
   *  That route's referenceMode already pre-fills the prospect relation on the form
   *  (ObjectFormComponent.findParentRelationField), and the GET behind it already
   *  tolerates the record not holding the prospect type yet (content-api TASK-0318).
   *  Without relatedClass, falls back to the same routing onMrdNavigate uses. */
  private onNavigateFromMenu(item: ClientLayoutItem): void {
    if (item.relatedClass) {
      this.router.navigate(['data', this.tenantCode, item.dataClass, this.id, item.relatedClass, 'new']);
      return;
    }
    if (item.navigationKind === 'OBJECT_FORM_DASHBOARD') {
      this.router.navigate(['data', this.tenantCode, item.dataClass, 'new']);
    } else if (item.navigationKind === 'DASHBOARD') {
      this.router.navigate([this.tenantCode], { queryParams: item.name ? { name: item.name } : {} });
    } else if (item.navigationKind === 'SYSTEM_DASHBOARD') {
      this.router.navigate(['system', this.tenantCode, item.dataClass]);
    } else {
      this.router.navigate([this.tenantCode, item.dataClass]);
    }
  }

  onMrdDownload(event: CustomEvent<{ href: string; fileName: string }>) {
    const { href } = event.detail;
    this.api.getDownloadLink(href).subscribe(url => window.open(url, '_blank', 'noopener,noreferrer'));
  }

  onMrdLoadImage(section: HTMLElement, event: CustomEvent<{ fieldName: string; href: string }>) {
    const { fieldName, href } = event.detail;
    this.api.getDownloadLink(href).subscribe(url => {
      (section as any).setImagePreview(fieldName, url);
    });
  }

  /** Answers a RELATED_OBJECT item's object/layout request. Always answered on the outer
   *  section — it forwards answers to nested sections whose events bubbled through it. */
  onMrdLoadObject(section: HTMLElement, event: CustomEvent<LoadObjectRequest>) {
    const { name } = event.detail;
    this.api.loadObject(event.detail)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ data, layout }) => (section as any).setObject(name, data, layout));
  }

  onMrdAttachments(event: CustomEvent<{ attachments: DocumentAttachment[] }>) {
    this.attachments = event.detail.attachments;
  }

  /** Answers mrd-layout-section's once-per-binding "may I create here" question
   *  (TASK-0285/TASK-0286): a coarse per-type/per-view capability for a top-level VIEW,
   *  or the parent-conditional form for a RELATED_VIEW. System routes carry no ACL
   *  (isAdmin getter above), so they're already admin-only via adminGuard — answer
   *  true there without a round trip. */
  onMrdViewCheckCapabilities(section: HTMLElement, event: CustomEvent<{ name: string; dataClass: string; refClass?: string; refId?: string; filterClass?: string }>) {
    const { name, dataClass, refClass, refId, filterClass } = event.detail;
    if (this.isSystemRoute) {
      (section as any).setViewCanCreate(name, true);
      return;
    }
    const capabilities$ = refClass && refId
      ? this.api.getRelatedViewCapabilities(this.tenantCode, refClass, refId, dataClass, filterClass)
      : this.api.getViewCapabilities(this.tenantCode, dataClass, filterClass);
    capabilities$.subscribe({
      next: (result) => (section as any).setViewCanCreate(name, result.mayCreate, result.allowedClasses),
      error: (err) => {
        console.error('[dashboard] capabilities laden mislukt', name, err);
        (section as any).setViewCanCreate(name, false);
      },
    });
  }

  onMrdLoadViewPage(section: HTMLElement, event: CustomEvent<{ name: string; page: number; path: string; qs: string; nodeId?: string }>) {
    const { name, page, path, qs, nodeId } = event.detail;
    const base = this.isSystemRoute ? 'system' : 'data';
    const url = `/${base}/${this.tenantCode}${path}${qs ? '?' + qs : ''}`;
    this.api.getObjectsByUrl(url).subscribe(response => {
      const rows: any[] = response._embedded ? Object.values(response._embedded)[0] as any[] ?? [] : [];
      const totalElements = response.page?.totalElements;
      // nodeId present → document-list folder load; routed to the tree node.
      (section as any).setViewPage(name, page, rows, totalElements, undefined, nodeId);
    });
  }

  async onMrdLoadViewDistinct(
    section: HTMLElement,
    event: CustomEvent<{ name: string; nodeId: string; distinct: string; path: string; qs: string }>
  ) {
    const { name, nodeId, path, qs } = event.detail;
    const base = this.isSystemRoute ? 'system' : 'data';
    const url = `/${base}/${this.tenantCode}${path}${qs ? '?' + qs : ''}`;
    try {
      const result = await firstValueFrom(this.api.getDistinctByUrl(url));
      await (section as any).setViewDistinct(name, nodeId, result?.values ?? []);
    } catch (err) {
      console.error('[dashboard] distinct laden mislukt', name, err);
    }
  }

  onMrdViewAction(event: CustomEvent<{ name: string; action: string; dataClass: string; path?: string; qs?: string; parentPath?: string | null; objectClass?: string | null }>) {
    const { action, dataClass, path, qs, parentPath, objectClass } = event.detail;
    if (action === 'export' && path) {
      const base = this.isSystemRoute ? 'system' : 'data';
      const url = `/${base}/${this.tenantCode}${path}${qs ? '?' + qs : ''}`;
      this.api.getDownloadLink(url).subscribe(exportUrl => window.open(exportUrl, '_blank', 'noopener,noreferrer'));
      return;
    }
    if (action === 'create') {
      const queryParams: Record<string, string> = {};
      if (objectClass) queryParams['objectClass'] = objectClass;
      if (this.isSystemRoute) {
        if (parentPath) {
          const segments = parentPath.split('/').filter(Boolean);
          this.router.navigate(['system', this.tenantCode, ...segments, dataClass, 'new'], { queryParams });
        } else {
          this.router.navigate(['system', this.tenantCode, dataClass, 'new'], { queryParams });
        }
      } else if (parentPath) {
        const segments = parentPath.split('/').filter(Boolean);
        this.router.navigate(['data', this.tenantCode, ...segments, dataClass, 'new'], { queryParams });
      } else {
        this.router.navigate(['data', this.tenantCode, dataClass, 'new'], { queryParams });
      }
    }
  }

  /** Generic in-place update of an existing object (e.g. a document dragged into
   *  another folder). Fire-and-forget PATCH; the component already updated its
   *  view optimistically. */
  onMrdUpdateObject(event: CustomEvent<{ href: string; values: Record<string, unknown> }>) {
    const { href, values } = event.detail;
    this.api.updateObjectByUrl(href, values).subscribe({
      error: err => console.error('[dashboard] object bijwerken mislukt', href, err),
    });
  }

  /** Confirmed delete from an embedded memo dialog. Fire-and-forget, same as
   *  onMrdUpdateObject — the row is already removed optimistically on the
   *  component side. */
  onMrdDeleteObject(event: CustomEvent<{ href: string }>) {
    const { href } = event.detail;
    this.api.deleteObject(href).subscribe({
      error: err => console.error('[dashboard] object verwijderen mislukt', href, err),
    });
  }

  /** An external file was dropped on a document view: upload it, then hand the
   *  file reference back to the component (same flow as mrd-form's mrdUpload). */
  async onMrdViewUpload(section: HTMLElement, event: CustomEvent<{ name: string; file: File }>) {
    const { name, file } = event.detail;
    try {
      const uploaded = await firstValueFrom(this.api.upload(this.tenantCode, file));
      await (section as any).setViewFileReference(name, uploaded);
    } catch (err) {
      console.error('[dashboard] bestand uploaden mislukt', name, err);
    }
  }

  /** Create a new document from the uploaded file. On success the created href is
   *  returned so the optimistic row becomes navigable. A 400 (missing required
   *  fields) is where the create-form fallback should open. */
  async onMrdCreateObject(section: HTMLElement, event: CustomEvent<{ name: string; path: string; values: Record<string, unknown> }>) {
    const { name, path, values } = event.detail;
    const base = this.isSystemRoute ? 'system' : 'data';
    const url = `/${base}/${this.tenantCode}${path}`;
    try {
      const href = await firstValueFrom(this.api.createObjectByUrl(url, values));
      await (section as any).setViewCreatedObject(name, href);
    } catch (err: any) {
      if (err?.status === 400) {
        // TODO: open the pre-filled create form (file + folder + container) so the
        // user can complete the required fields. Kept generic, out of the components.
        console.warn('[dashboard] document aanmaken vereist extra velden (400)', name, values);
      } else {
        console.error('[dashboard] document aanmaken mislukt', name, err);
      }
    }
  }

  async onMrdLoadViewAggregations(
    section: HTMLElement,
    event: CustomEvent<{ name: string; path: string; aggQs?: string }>
  ) {
    const { name, path, aggQs } = event.detail;
    const base = this.isSystemRoute ? 'system' : 'data';
    const url = `/${base}/${this.tenantCode}${path}/aggregations${aggQs ? '?' + aggQs : ''}`;
    try {
      const result = await firstValueFrom(this.api.getAggregationsByUrl(url));
      await (section as any).setViewAggregations(name, result);
    } catch (err) {
      console.error('[dashboard] aggregaties laden mislukt', name, err);
    }
  }

  onMrdSearch(section: HTMLElement, event: CustomEvent<{ query: string; dataClass: string }>) {
    const { query, dataClass } = event.detail;
    const url = this.isSystemRoute
      ? this.api.getSystemSearchUrl(this.tenantCode, dataClass, query)
      : this.api.getSearchUrl(this.tenantCode, dataClass, query);
    this.api.getObjectsByUrl(url).subscribe(response => {
      const items: any[] = response._embedded ? Object.values(response._embedded)[0] as any[] ?? [] : [];
      const results: RelationSearchResult[] = items.map((item: any) => ({
        id: item._links._self.href,
        label: item.name,
      }));
      (section as any).setSearchResults(results);
    });
  }

  onMrdMergeSearch(event: CustomEvent<{ query: string; objectClass: string; mostSignificantClass?: string }>) {
    const { query, mostSignificantClass } = event.detail;
    this.api.getObjectsByUrl(this.api.getSearchUrl(this.tenantCode, mostSignificantClass ?? this.type, query))
      .subscribe(result => {
        const embedded = result._embedded ? Object.values(result._embedded)[0] : [];
        (this.mergeViewEl?.nativeElement as any)?.setSearchResults(Array.isArray(embedded) ? embedded : []);
      });
  }

  onMrdMerge(event: CustomEvent<{ winnerHref: string; loserHref: string; patch: Record<string, unknown> }>) {
    const { winnerHref, loserHref, patch } = event.detail;
    this.api.mergeObject(winnerHref, loserHref, patch).subscribe({
      next: () => {
        this.mergeOverlayOpen = false;
        this.refresh();
      },
      error: err => {
        console.error('[dashboard] merge mislukt', winnerHref, loserHref, err);
        this.notificationService.show({ message: 'Samenvoegen is mislukt.', level: 'ERROR' });
      },
    });
  }

  closeMergeOverlay(): void {
    this.mergeOverlayOpen = false;
  }

  cancelDelete(): void {
    this.deleteConfirmOpen = false;
  }

  confirmDelete(): void {
    this.deleteConfirmOpen = false;
    this.api.deleteObject(this.dataObject!._links._self.href).subscribe({
      next: () => this.router.navigate([this.tenantCode]),
      error: error => this.notificationService.showServerError(error, 'delete.failed', 'WARNING'),
    });
  }

  /** Fetches the FORM layout (not the dashboard layout) so formula/derived fields
   *  like `name` are already excluded server-side, then flattens SECTION/GROUP
   *  wrapping down to the FIELD/RELATION items mrd-merge-view expects. */
  private openMergeOverlay(): void {
    this.api.getDashboardByUrl(this.dataObject!._links._form.href).subscribe(metadata => {
      this.mergeFields = flattenLayoutItems(metadata.layouts.flatMap(l => l.items));
      this.mergeOverlayOpen = true;
    });
  }

  toggleActionsDropdown() {
    this.actionsDropdownOpen = !this.actionsDropdownOpen;
  }

  onActionFromDropdown(action: ClientLayoutItem) {
    this.actionsDropdownOpen = false;

    if (action.type === ClientLayoutItemType.NAVIGATE) {
      this.onNavigateFromMenu(action);
      return;
    }

    if (action.actionType === 'REMOVE') {
      this.deleteConfirmOpen = true;
      return;
    }

    if (action.actionType === 'MERGE') {
      this.openMergeOverlay();
      return;
    }


    if (action.actionType === 'REPORT') {
      const selfHref = this.dataObject!._links._self.href;
      const language = this.appService.getLanguage();
      const langParam = language ? `?language=${language}` : '';
      const reportUrl = `${selfHref}/reports/${action.name}${langParam}`;
      this.api.getReport(reportUrl).subscribe(url => window.open(url, '_blank', 'noopener,noreferrer'));
      return;
    }

    this.api.doAction(this.dataObject?._links._scripts.href, action.name ?? '')
      .subscribe(notification => {
        if (notification) this.notificationService.show(notification);
      });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.actionsDropdownOpen && this.actionsContainer &&
        !this.actionsContainer.nativeElement.contains(event.target)) {
      this.actionsDropdownOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.actionsDropdownOpen = false;
    if (this.openOverlayHref) {
      this.closeOverlay();
    }
    if (this.mergeOverlayOpen) {
      this.closeMergeOverlay();
    }
  }

  @HostListener('document:keydown.arrowleft', ['$event'])
  onArrowLeft(event: Event) {
    if (this.ignoreArrowNav(event) || !this.overlayNavLinks?.prev) return;
    event.preventDefault();
    this.overlayPrev();
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  onArrowRight(event: Event) {
    if (this.ignoreArrowNav(event) || !this.overlayNavLinks?.next) return;
    event.preventDefault();
    this.overlayNext();
  }

  /** Arrow keys mirror the backdrop's prev/next buttons: only while this instance
   *  owns an open overlay with a list context, and never while the user is typing
   *  or moving the caret in a form control. */
  private ignoreArrowNav(event: Event): boolean {
    if (!this.openOverlayHref || !this.openOverlayListContext) return true;
    const target = event.target as HTMLElement | null;
    return !!target?.closest('input, textarea, select, [contenteditable]');
  }

  closeOverlay(): void {
    if (this.overlayHref) {
      this.overlayClose.emit();
      return;
    }
    this.openOverlayHref = null;
    this.openOverlayListContext = null;
    this.overlayNavLinks = null;
  }

  /** Re-emitted from a nested overlay's own list click (an overlay opened from
   *  within an already-open overlay) — mirrors the top-level onMrdNavigate branch. */
  onOverlayNavigate(nav: { href: string; listContext?: ListNavigationContext }): void {
    this.openOverlayHref = nav.href;
    this.setOverlayListContext(nav.listContext);
  }

  overlayPrev(): void {
    this.followOverlayLink(this.overlayNavLinks?.prev);
  }

  overlayNext(): void {
    this.followOverlayLink(this.overlayNavLinks?.next);
  }

  private setOverlayListContext(listContext?: ListNavigationContext): void {
    this.openOverlayListContext = listContext ?? null;
    this.overlayNavLinks = null;
    if (listContext) this.loadOverlayNavLinks(listContext);
  }

  /** One `size=1` fetch to discover this record's exact HAL next/prev siblings —
   *  no total-count bookkeeping needed, just the presence of the links. */
  private loadOverlayNavLinks(ctx: ListNavigationContext): void {
    const base = this.isSystemRoute ? 'system' : 'data';
    const url = `/${base}/${this.tenantCode}${ctx.path}?${ctx.qs}${ctx.qs ? '&' : ''}page=${ctx.index}&size=1`;
    this.api.getObjectsByUrl(url).subscribe({
      next: response => {
        this.overlayNavLinks = { next: response._links?.next, prev: response._links?.prev };
      },
      error: err => console.error('[dashboard] overlay nav-links laden mislukt', url, err),
    });
  }

  /** Follows a HAL next/prev link directly — same idiom as ObjectSearchComponent.navigate() —
   *  and updates openOverlayHref plus the links for the next step. */
  private followOverlayLink(link?: { href: string }): void {
    if (!link) return;
    this.api.getObjectsByUrl(link.href).subscribe({
      next: response => {
        const rows: any[] = response._embedded ? Object.values(response._embedded)[0] as any[] ?? [] : [];
        const record = rows[0];
        if (!record?._links?._self?.href) return;
        this.openOverlayHref = record._links._self.href;
        this.overlayNavLinks = { next: response._links?.next, prev: response._links?.prev };
      },
      error: err => console.error('[dashboard] overlay navigeren mislukt', link.href, err),
    });
  }

  onEdit() {
    if (this.overlayHref) {
      this.editingInOverlay = true;
      return;
    }
    this.router.navigate(['edit'], { relativeTo: this.route });
  }

  onOverlayEditSaved(): void {
    this.editingInOverlay = false;
    this.refresh();
  }

  onOverlayEditCancelled(): void {
    this.editingInOverlay = false;
  }

  /** The record being edited in the overlay was deleted — there is nothing left
   *  to refresh() into, so close the overlay and go home instead. */
  onOverlayEditDeleted(): void {
    this.editingInOverlay = false;
    this.router.navigate([this.tenantCode]);
  }
}

/** Recursively drops SECTION/GROUP wrapping down to the leaf FIELD/RELATION items —
 *  mrd-merge-view expects a flat schema list, the same assumption as mrd-table's columns. */
function flattenLayoutItems(items: ClientLayoutItem[] = []): ClientLayoutItem[] {
  return items.flatMap(item =>
    item.type === ClientLayoutItemType.SECTION || item.type === ClientLayoutItemType.GROUP
      ? flattenLayoutItems(item.items ?? [])
      : item.type === ClientLayoutItemType.FIELD || item.type === ClientLayoutItemType.RELATION
        ? [item]
        : [],
  );
}
